import { useEffect, useState } from 'react';

/**
 * Resilient <img> wrapper for remote URLs we don't fully control (review media, location media,
 * any third-party CDN). Defaults are picked for Google FIFE — the most flake-prone source we
 * deal with — but the component is platform-agnostic:
 *
 *   - `referrerPolicy="no-referrer"` (overridable) — Google FIFE rejects requests carrying a
 *     Referer header. Most other CDNs don't care; anti-hotlink schemes that require Referer
 *     can be opted into by passing `referrerPolicy="origin"` or `"strict-origin-when-cross-origin"`.
 *   - `loading="lazy"` — defers off-screen images.
 *   - On error, the wrapper renders `fallback` (null by default, i.e. the image vanishes) and
 *     invokes the parent's `onError` callback if provided.
 *   - Render the optional `placeholder` while loading; the underlying <img> renders concurrently
 *     but has no visible content yet, so the placeholder is what the user sees.
 *
 * IMPORTANT: do NOT hide the <img> via `display: none` while loading — that removes it from the
 * layout and the browser stops scheduling lazy fetches. The image must remain in the DOM with a
 * positionable bounding box for `loading="lazy"` to ever resolve.
 */
const SafeImage = ({
    src,
    alt = '',
    onLoad,
    onError,
    placeholder = null,
    fallback = null,
    referrerPolicy = 'no-referrer',
    ...imgProps
}) => {
    const [status, setStatus] = useState('loading');

    useEffect(() => {
        setStatus('loading');
    }, [src]);

    const handleLoad = e => {
        setStatus('loaded');
        onLoad?.(e);
    };

    const handleError = e => {
        setStatus('failed');
        onError?.(e);
    };

    if (status === 'failed') {
        return fallback;
    }

    return (
        <>
            {status === 'loading' && placeholder}
            <img
                {...imgProps}
                src={src}
                alt={alt}
                loading="lazy"
                referrerPolicy={referrerPolicy}
                onLoad={handleLoad}
                onError={handleError}
            />
        </>
    );
};

export default SafeImage;
